import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Product {
  id: string;
  title: string;
  description: string;
  image: string;
  href: string;
  ringColor: string;
  hoverBg: string;
  textColor: string;
}

const products: Product[] = [
  {
    id: "studio",
    title: "gimmefy Studio",
    description: "The command center for marketing content",
    image: "/lovable-uploads/934ac46f-c966-480e-923a-fb538d02db36.jpg",
    href: "/studio",
    ringColor: "hover:ring-primary",
    hoverBg: "hover:bg-primary/5",
    textColor: "text-primary"
  },
  {
    id: "playbooks",
    title: "gimmefy Playbooks",
    description: "One-click marketing workflow stacks",
    image: "/lovable-uploads/18cd406b-3dd2-4f45-80e1-83f5e5f59739.jpg",
    href: "/playbooks",
    ringColor: "hover:ring-blue-500",
    hoverBg: "hover:bg-blue-500/5",
    textColor: "text-blue-500"
  },
  {
    id: "visual-lab",
    title: "gimmefy Visual Lab",
    description: "AI-powered visual creation and editing",
    image: "/lovable-uploads/615b6300-1f3b-4ff6-b674-a7761e0fe271.jpg",
    href: "/visual-lab",
    ringColor: "hover:ring-emerald-500",
    hoverBg: "hover:bg-emerald-500/5",
    textColor: "text-emerald-500"
  },
  {
    id: "agents",
    title: "gimmefy Agents",
    description: "Industry-standard briefs, specialized marketing AI",
    image: "/lovable-uploads/e64282f4-5403-4f77-813c-4b5543edffc4.png",
    href: "/agents",
    ringColor: "hover:ring-amber-500",
    hoverBg: "hover:bg-amber-500/5",
    textColor: "text-amber-500"
  }
];

interface DiscoverMoreSectionProps {
  currentPage: "studio" | "playbooks" | "visual-lab" | "agents";
}

const DiscoverMoreSection = ({ currentPage }: DiscoverMoreSectionProps) => {
  const navigate = useNavigate();
  
  // Filter out current page and show remaining 4 products
  const filteredProducts = products.filter(p => p.id !== currentPage);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">Explore gimmefy</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Discover more of gimmefy
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore the full suite of marketing tools
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {filteredProducts.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => navigate(feature.href)}
              className={`group cursor-pointer rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ring-2 ring-transparent ${feature.ringColor} ${feature.hoverBg}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
                <span className={`inline-flex items-center text-sm font-medium ${feature.textColor} group-hover:underline`}>
                  Learn more
                  <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DiscoverMoreSection;
